import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Table, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { taskService } from '../../services/api';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import './EmployeePerformance.css';

// Register Chart.js components
ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const EmployeePerformance = () => {
  const { currentUser } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statistics, setStatistics] = useState({
    tasksCompleted: 0,
    tasksInProgress: 0,
    tasksPending: 0,
    onTimeCompletion: 0,
    avgCompletionTime: 0,
    performanceScore: 0
  });

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setLoading(true);
        const response = await taskService.getAssignedTasks();
        const tasksData = response.data?.data || [];
        setTasks(tasksData);
        calculateStatistics(tasksData);
      } catch (err) {
        console.error('Error fetching tasks:', err);
        setError('Failed to load performance data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchTasks();
    }
  }, [currentUser]);

  const calculateStatistics = (taskData) => {
    const completedTasks = taskData.filter(task => task.status === 'completed');
    const inProgressTasks = taskData.filter(task => task.status === 'in_progress');
    const pendingTasks = taskData.filter(task => 
      task.status === 'todo' || task.status === 'pending' || task.status === ''
    );

    // Calculate on-time completion rate
    let onTimeCount = 0;
    completedTasks.forEach(task => {
      if (task.due_date && task.updated_at) {
        const dueDate = new Date(task.due_date);
        const completionDate = new Date(task.updated_at);
        if (completionDate <= dueDate) {
          onTimeCount++;
        }
      }
    });

    const onTimeRate = completedTasks.length > 0 
      ? Math.round((onTimeCount / completedTasks.length) * 100) 
      : 0;

    // Calculate average completion time (in days)
    let totalDays = 0;
    completedTasks.forEach(task => {
      if (task.created_at && task.updated_at) {
        const startDate = new Date(task.created_at);
        const endDate = new Date(task.updated_at);
        const days = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));
        totalDays += days;
      }
    });

    const avgDays = completedTasks.length > 0 
      ? Math.round(totalDays / completedTasks.length) 
      : 0;

    // Calculate performance score (simple formula)
    const completionRate = taskData.length > 0 
      ? (completedTasks.length / taskData.length) * 100 
      : 0;
    
    const performanceScore = Math.round((completionRate * 0.4) + (onTimeRate * 0.6));

    setStatistics({
      tasksCompleted: completedTasks.length,
      tasksInProgress: inProgressTasks.length,
      tasksPending: pendingTasks.length,
      onTimeCompletion: onTimeRate,
      avgCompletionTime: avgDays,
      performanceScore: performanceScore
    });
  };

  // Charts data
  const statusData = {
    labels: ['Completed', 'In Progress', 'Pending'],
    datasets: [{
      data: [statistics.tasksCompleted, statistics.tasksInProgress, statistics.tasksPending],
      backgroundColor: ['#28a745', '#007bff', '#6c757d'],
      borderWidth: 1
    }]
  };

  const priorityData = tasks.length ? {
    labels: ['Low', 'Medium', 'High', 'Urgent'],
    datasets: [{
      label: 'Tasks by Priority',
      data: [
        tasks.filter(task => task.priority === 'low').length,
        tasks.filter(task => task.priority === 'medium').length,
        tasks.filter(task => task.priority === 'high').length,
        tasks.filter(task => task.priority === 'urgent').length,
      ],
      backgroundColor: ['#17a2b8', '#ffc107', '#fd7e14', '#dc3545']
    }]
  } : { labels: [], datasets: [] };

  // Get color based on score
  const getScoreColor = (score) => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'primary';
    if (score >= 50) return 'warning';
    return 'danger';
  };

  // Get recently completed tasks (last 5)
  const recentlyCompletedTasks = tasks
    .filter(task => task.status === 'completed')
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
    .slice(0, 5);

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading your performance data...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="my-4">
        <Col>
          <h1 className="h3 mb-0 text-gray-800">
            <i className="fas fa-chart-line me-2"></i>
            My Performance Dashboard
          </h1>
          <p className="text-muted">Track your task performance and statistics</p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger">{error}</Alert>
      )}

      {/* Performance Score */}
      <Row className="performance-score">
        <Col md={3}>
          <div className="stat-box text-center">
            <div className="text-muted mb-2">Performance Score</div>
            <h3 className={`text-${getScoreColor(statistics.performanceScore)}`}>
              {statistics.performanceScore}%
            </h3>
            <div className="text-muted small">Based on completion and timeliness</div>
          </div>
        </Col>
        <Col md={3}>
          <div className="stat-box text-center">
            <div className="text-muted mb-2">On-Time Completion</div>
            <h3 className="text-primary">{statistics.onTimeCompletion}%</h3>
            <div className="text-muted small">Tasks completed before deadline</div>
          </div>
        </Col>
        <Col md={3}>
          <div className="stat-box text-center">
            <div className="text-muted mb-2">Avg. Days to Complete</div>
            <h3 className="text-info">{statistics.avgCompletionTime}</h3>
            <div className="text-muted small">Working days per task</div>
          </div>
        </Col>
        <Col md={3}>
          <div className="stat-box text-center">
            <div className="text-muted mb-2">Total Tasks</div>
            <h3 className="text-dark">{tasks.length}</h3>
            <div className="text-muted small">Assigned to you</div>
          </div>
        </Col>
      </Row>

      {/* Charts */}
      <Row>
        <Col md={6}>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-light">
              <h5 className="mb-0"><i className="fas fa-chart-pie me-2"></i>Tasks by Status</h5>
            </Card.Header>
            <Card.Body>
              {tasks.length > 0 ? (
                <div style={{ height: '250px' }}>
                  <Pie 
                    data={statusData} 
                    options={{ 
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom'
                        }
                      }
                    }}
                  />
                </div>
              ) : (
                <Alert variant="info">No task data available to display.</Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-light">
              <h5 className="mb-0"><i className="fas fa-chart-bar me-2"></i>Tasks by Priority</h5>
            </Card.Header>
            <Card.Body>
              {tasks.length > 0 ? (
                <div style={{ height: '250px' }}>
                  <Bar 
                    data={priorityData}
                    options={{
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            precision: 0
                          }
                        }
                      }
                    }}
                  />
                </div>
              ) : (
                <Alert variant="info">No task data available to display.</Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recent Completions */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-light">
              <h5 className="mb-0"><i className="fas fa-trophy me-2"></i>Recent Completions</h5>
            </Card.Header>
            <Card.Body>
              {recentlyCompletedTasks.length > 0 ? (
                <Table responsive className="table-hover">
                  <thead>
                    <tr>
                      <th>Task</th>
                      <th>Priority</th>
                      <th>Completed Date</th>
                      <th>On Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentlyCompletedTasks.map(task => {
                      const dueDate = task.due_date ? new Date(task.due_date) : null;
                      const completionDate = task.updated_at ? new Date(task.updated_at) : null;
                      const isOnTime = dueDate && completionDate && completionDate <= dueDate;
                      
                      return (
                        <tr key={task.id}>
                          <td>{task.title}</td>
                          <td>
                            <Badge 
                              bg={task.priority === 'high' || task.priority === 'urgent' 
                                ? 'danger' 
                                : task.priority === 'medium' 
                                  ? 'warning' 
                                  : 'info'}
                            >
                              {task.priority || 'low'}
                            </Badge>
                          </td>
                          <td>{completionDate ? completionDate.toLocaleDateString() : 'N/A'}</td>
                          <td>
                            {dueDate ? (
                              <Badge bg={isOnTime ? 'success' : 'danger'}>
                                {isOnTime ? 'Yes' : 'No'}
                              </Badge>
                            ) : 'N/A'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              ) : (
                <Alert variant="info">You haven't completed any tasks yet.</Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Productivity Timeline */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-light">
              <h5 className="mb-0"><i className="fas fa-history me-2"></i>Productivity Timeline</h5>
            </Card.Header>
            <Card.Body>
              {tasks.length > 0 ? (
                <ul className="timeline">
                  {tasks
                    .filter(task => task.status === 'completed')
                    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
                    .slice(0, 8)
                    .map(task => (
                      <li key={task.id} className="timeline-item">
                        <div className="timeline-date">
                          {new Date(task.updated_at).toLocaleDateString()}
                        </div>
                        <div className="timeline-content">
                          <strong>{task.title}</strong> - Task completed
                        </div>
                      </li>
                    ))}
                </ul>
              ) : (
                <Alert variant="info">No activity to display.</Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default EmployeePerformance; 